import { CLASS_IDS, DEFAULT_CLASS, PALETTE, TEXT_CONTRAST, contrast, type ClassId, type Hex } from "@eldrun/content";

/** Contraste mínimo de componente de interface e de anel de foco (WCAG 1.4.11). */
export const UI_CONTRAST = 3;

export type Mode = "light" | "dark";

/**
 * As superfícies de cada modo onde o acento aparece como texto, convertidas do `theme.css` (o teste
 * confere que os valores batem com o arquivo): fundo, superfície (`--card`), superfície elevada
 * (`--popover`) e os dois painéis cinza (`--muted`, `--secondary`).
 */
export const SURFACES: Readonly<Record<Mode, { readonly background: Hex; readonly surfaces: readonly Hex[] }>> = {
  light: { background: oklchGray(0.9612), surfaces: [oklchGray(0.9612), oklchGray(0.99), oklchGray(0.99), oklchGray(0.9219), oklchGray(0.8607)] },
  dark: { background: oklchGray(0.1448), surfaces: [oklchGray(0.1448), oklchGray(0.17), oklchGray(0.19), oklchGray(0.2046), oklchGray(0.2686)] },
};

/** `oklch(L C h)` → `#rrggbb` (OKLab → sRGB linear → sRGB), cortando o que sai da gama. */
export function oklchHex(l: number, c: number, h: number): Hex {
  const rad = (h * Math.PI) / 180;
  const a = c * Math.cos(rad);
  const b = c * Math.sin(rad);
  const l1 = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m1 = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s1 = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  const linear = [4.0767416621 * l1 - 3.3077115913 * m1 + 0.2309699292 * s1, -1.2684380046 * l1 + 2.6097574011 * m1 - 0.3413193965 * s1, -0.0041960863 * l1 - 0.7034186147 * m1 + 1.707614701 * s1];
  const byte = (v: number) => {
    const x = Math.min(1, Math.max(0, v));
    const srgb = x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
    return Math.round(srgb * 255)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${linear.map(byte).join("")}`;
}

/** As duas tintas de texto possíveis sobre o preenchimento do acento: o fundo escuro do tema e o branco. */
export const INKS: readonly Hex[] = [oklchGray(0.1448), oklchGray(1)];

export interface AccentTokens {
  /** A cor pura da classe: preenchimento, borda, brilho e anel de foco no escuro. */
  readonly accent: Hex;
  /** Texto sobre o preenchimento do acento. */
  readonly foreground: Hex;
  /** O acento como texto (e anel de foco no claro): a variante que passa de 4,5:1 nas superfícies do modo. */
  readonly text: Record<Mode, Hex>;
  /** O anel de foco de cada modo (3:1 contra todas as superfícies do modo). */
  readonly ring: Record<Mode, Hex>;
}

/** `oklch(L 0 0)` (cinza, sem croma) → `#rrggbb`: no cinza o OKLab dá a luminância linear L³. */
export function oklchGray(l: number): Hex {
  const linear = l * l * l;
  const srgb = linear <= 0.0031308 ? 12.92 * linear : 1.055 * linear ** (1 / 2.4) - 0.055;
  const byte = Math.round(Math.min(1, Math.max(0, srgb)) * 255)
    .toString(16)
    .padStart(2, "0");
  return `#${byte}${byte}${byte}`;
}

function mix(from: Hex, to: Hex, t: number): Hex {
  const a = [1, 3, 5].map((i) => parseInt(from.slice(i, i + 2), 16));
  const b = [1, 3, 5].map((i) => parseInt(to.slice(i, i + 2), 16));
  return `#${a.map((c, i) => Math.round(c + (b[i]! - c) * t).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * A variante de `hex` que passa de `target`:1 contra todas as `surfaces`: o próprio `hex` se já passa;
 * senão, o primeiro passo de 1 % em direção a `toward` (branco no escuro, preto no claro) que passa.
 */
export function readableOn(hex: Hex, surfaces: readonly Hex[], target: number, toward: Hex): Hex {
  const passes = (c: Hex) => surfaces.every((s) => contrast(c, s) >= target);
  if (passes(hex)) return hex;
  for (let step = 1; step <= 100; step++) {
    const mixed = mix(hex, toward, step / 100);
    if (passes(mixed)) return mixed;
  }
  return toward;
}

export function accentTokens(id: ClassId): AccentTokens {
  return accentTokensFor(PALETTE.classes[id].hex.toLowerCase() as Hex);
}

/**
 * Os mesmos tokens para qualquer cor de acento: a variante daltônica de uma classe (F60) ou o neutro do
 * jogo antes de escolher a classe. O jogo aplica pelo CSSOM (`--accent`, `--accent-foreground`,
 * `--accent-text`, `--ring`).
 */
export function accentTokensFor(hex: Hex): AccentTokens {
  const accent = hex.toLowerCase() as Hex;
  const foreground = INKS.reduce((best, ink) => (contrast(ink, accent) > contrast(best, accent) ? ink : best));
  const text = {
    light: readableOn(accent, SURFACES.light.surfaces, TEXT_CONTRAST, "#000000"),
    dark: readableOn(accent, SURFACES.dark.surfaces, TEXT_CONTRAST, "#ffffff"),
  };
  // O anel fica 2 px fora do elemento, sobre a superfície do pai: confere contra todas as do modo.
  const ring = {
    light: readableOn(accent, SURFACES.light.surfaces, UI_CONTRAST, "#000000"),
    dark: readableOn(accent, SURFACES.dark.surfaces, UI_CONTRAST, "#ffffff"),
  };
  return { accent, foreground, text, ring };
}

/**
 * O CSS dos acentos. Cada classe define a cor, a tinta (e o filtro que pinta ícones nela) e as variantes dos dois modos (`--accent-text-light`
 * e `-dark`, `--ring-light` e `-dark`); dois blocos escolhem o modo: o claro em `:root` e em todo
 * `[data-classe]`, o escuro em `.dark` e em todo `[data-classe]` dentro dele. A troca é redeclarada
 * em cada elemento com `data-classe` (e não só na raiz) para que um cartão de classe tenha o próprio
 * acento por inteiro, e em cada `.dark` para que uma região escura fora da raiz leia a variante certa
 * da classe herdada. Sem `data-classe`, vale a classe padrão.
 */
export function accentCss(): string {
  const lines = [
    "/* Gerado por `pnpm --filter @eldrun/web-ui accents` a partir de PALETTE.classes (ADR 0017). Não editar. */",
  ];
  const ordered = [DEFAULT_CLASS, ...CLASS_IDS.filter((id) => id !== DEFAULT_CLASS)];
  for (const id of ordered) {
    const t = accentTokens(id);
    lines.push(
      `${id === DEFAULT_CLASS ? ":root, " : ""}[data-classe="${id}"] {`,
      `  --accent: ${t.accent};`,
      `  --accent-foreground: ${t.foreground};`,
      // ícones em <img> (SVG de cor fixa) sobre o acento: o filtro os pinta na tinta (preta ou branca)
      `  --accent-icon-filter: ${t.foreground === INKS[1] ? "brightness(0) invert(1)" : "brightness(0)"};`,
      `  --accent-text-light: ${t.text.light};`,
      `  --accent-text-dark: ${t.text.dark};`,
      `  --ring-light: ${t.ring.light};`,
      `  --ring-dark: ${t.ring.dark};`,
      "}",
    );
  }
  lines.push(
    ":root, [data-classe] {",
    "  --accent-text: var(--accent-text-light);",
    "  --ring: var(--ring-light);",
    "}",
    ".dark, .dark [data-classe] {",
    "  --accent-text: var(--accent-text-dark);",
    "  --ring: var(--ring-dark);",
    "}",
  );
  return `${lines.join("\n")}\n`;
}
